/**
 * SlideHapticsController - Haptic feedback during piece sliding
 * Provides tactile "ticks" as the piece slides across the surface.
 */

import { HapticsManager } from './HapticsManager';

/**
 * Configuration for slide haptics
 */
export interface SlideHapticsConfig {
  /** Minimum pixels between haptic ticks */
  tickInterval: number;
  /** Enable/disable slide haptics */
  enabled: boolean;
}

const DEFAULT_CONFIG: SlideHapticsConfig = {
  tickInterval: 15,
  enabled: true,
};

/**
 * Controls haptic feedback during sliding operations
 */
export class SlideHapticsController {
  private haptics: HapticsManager;
  private config: SlideHapticsConfig;
  private lastTickPosition: number = 0;
  private isSliding: boolean = false;

  constructor(haptics: HapticsManager, config?: Partial<SlideHapticsConfig>) {
    this.haptics = haptics;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Start tracking slide position
   * @param initialPosition - Starting X position of the sliding piece
   */
  startSlide(initialPosition: number): void {
    this.lastTickPosition = initialPosition;
    this.isSliding = true;
  }

  /**
   * Update slide position and trigger haptic ticks
   * @param currentPosition - Current X position of the sliding piece
   */
  updateSlide(currentPosition: number): void {
    if (!this.isSliding || !this.config.enabled) return;

    const distance = Math.abs(currentPosition - this.lastTickPosition);

    if (distance >= this.config.tickInterval) {
      this.haptics.slideTick();
      this.lastTickPosition = currentPosition;
    }
  }

  /**
   * End slide tracking
   */
  endSlide(): void {
    this.isSliding = false;
    this.lastTickPosition = 0;
  }

  /**
   * Check if currently in sliding state
   */
  isActive(): boolean {
    return this.isSliding;
  }

  /**
   * Set the pixel interval between haptic ticks
   */
  setTickInterval(interval: number): void {
    this.config.tickInterval = Math.max(5, interval);
  }

  /**
   * Get current tick interval
   */
  getTickInterval(): number {
    return this.config.tickInterval;
  }

  /**
   * Enable or disable slide haptics
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if slide haptics are enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }
}
